import { useEffect, useRef, useState } from 'react';
import { EditorState } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { syntaxHighlighting, defaultHighlightStyle } from '@codemirror/language';
import { houseopsTheme } from './theme.ts';
import { liveMarkdownPreview } from './live-preview.ts';
import { houseopsKeymap } from './keymap.ts';
import { pastePolicy } from './paste.ts';
import { MarkdownToolbar } from './toolbar.tsx';
import type { MarkdownEditorProps } from './types.ts';

function resolveToolbarPosition(
  prop: MarkdownEditorProps['toolbar'],
): 'top' | 'bottom' | 'none' {
  if (!prop || prop === 'none') return 'none';
  if (prop !== 'auto') return prop;
  if (typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches) {
    return 'bottom';
  }
  return 'top';
}

export function MarkdownEditor(props: MarkdownEditorProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(props.onChange);
  const onSaveRef = useRef(props.onSave);
  const onTruncateRef = useRef(props.onTruncate);
  onChangeRef.current = props.onChange;
  onSaveRef.current = props.onSave;
  onTruncateRef.current = props.onTruncate;

  const [, forceRender] = useState(0);
  const toolbarPos = resolveToolbarPosition(props.toolbar ?? 'auto');

  useEffect(() => {
    if (!hostRef.current) return;

    const state = EditorState.create({
      doc: props.value,
      extensions: [
        history(),
        keymap.of([
          {
            key: 'Mod-s',
            preventDefault: true,
            run: () => {
              onSaveRef.current?.();
              return true;
            },
          },
        ]),
        houseopsKeymap,
        keymap.of([...historyKeymap, ...defaultKeymap]),
        markdown({ base: markdownLanguage }),
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
        liveMarkdownPreview,
        pastePolicy({
          maxLength: props.maxLength,
          onTruncate: (info) => onTruncateRef.current?.(info),
        }),
        houseopsTheme,
        EditorView.lineWrapping,
        EditorView.editable.of(!props.readOnly),
        EditorView.contentAttributes.of({
          'aria-label': props.placeholder ?? 'Markdown editor',
          spellcheck: 'true',
        }),
        EditorView.updateListener.of((u) => {
          if (u.docChanged) {
            onChangeRef.current(u.state.doc.toString());
          }
        }),
      ],
    });

    const view = new EditorView({ state, parent: hostRef.current });
    viewRef.current = view;
    // Re-render so the toolbar sees the live ref.
    forceRender((n) => n + 1);
    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // The CM instance is created once; subsequent prop changes are
    // synced via the value-sync effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external value changes (e.g. async fetch hydrating the
  // edit page). Skip when the change originated from the editor
  // itself — the doc string already matches.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    if (view.state.doc.toString() === props.value) return;
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: props.value },
    });
  }, [props.value]);

  return (
    <div className={['md-editor', props.className].filter(Boolean).join(' ')}>
      {toolbarPos === 'top' ? (
        <MarkdownToolbar viewRef={viewRef} position="top" />
      ) : null}
      <div
        ref={hostRef}
        className="md-editor-host"
        style={{ minHeight: props.minHeight ?? '320px' }}
      />
      {toolbarPos === 'bottom' ? (
        <MarkdownToolbar viewRef={viewRef} position="bottom" />
      ) : null}
    </div>
  );
}
